import { useEffect, useRef } from 'react'
import { Cohort } from '@afterpaytouch/portal-api/types'
import { useLDClient } from '@afterpaytouch/feature'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { Features } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import { newPersistentSession, useAppDispatch, useAuthentication, useConsumerSelector, useCohort, useInitialData, useIsTrustedCheckout } from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { isAfterpayInAppBrowser, isCashAppInAppBrowser } from '../../utils/device'
import { consumeInteractiveLoginSignal } from './loginSignal'
import { markRememberMeMinted } from './issuanceSignal'
import { hasOptedOutRememberMe } from './optOut'

export type AutoRememberMeSuppressReason =
  | 'alreadyPersistent'
  | 'trustedCheckout'
  | 'nonApCohort'
  | 'iframe'
  | 'inAppBrowser'
  | 'deviceOptOut'
  | 'flagOff'
  | 'serverWithheld'

export const useMintPersistentSessionOnLogin = (): void => {
  const dispatch = useAppDispatch()
  const { persistent } = useAuthentication()
  const isTrustedCheckout = useIsTrustedCheckout()
  const cohort = useCohort()
  const { isIframe } = useInitialData()
  const { uuid, hasFeatureFlagsReady } = useConsumerSelector()
  const ldClient = useLDClient()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const loginPendingMint = useRef(false)

  useEffect(() => {
    if (consumeInteractiveLoginSignal('remember-me-mint')) {
      loginPendingMint.current = true
    }
    if (!loginPendingMint.current) {
      return
    }
    const suppress = (suppressReason: AutoRememberMeSuppressReason): void => {
      loginPendingMint.current = false
      logEvent(TrackingEvent.AUTO_REMEMBER_ME_SUPPRESSED, { suppressReason })
    }
    if (persistent || isTrustedCheckout) {
      suppress(persistent ? 'alreadyPersistent' : 'trustedCheckout')
      return
    }
    if (cohort !== Cohort.APOnly) {
      suppress('nonApCohort')
      return
    }
    if (isIframe) {
      suppress('iframe')
      return
    }
    if (isAfterpayInAppBrowser() || isCashAppInAppBrowser()) {
      suppress('inAppBrowser')
      return
    }
    if (hasOptedOutRememberMe()) {
      suppress('deviceOptOut')
      return
    }
    if (isUndefinedOrEmpty(uuid) || ldClient === undefined) {
      return
    }
    const context = ldClient.getContext?.() as { key?: string } | undefined
    if (context?.key !== uuid || hasFeatureFlagsReady !== true) {
      return
    }
    loginPendingMint.current = false

    void (async () => {
      try {
        if (ldClient.variation(Features.CHECKOUT_LONG_LIVE_SESSION_ENABLED, false) !== true) {
          logEvent(TrackingEvent.AUTO_REMEMBER_ME_SUPPRESSED, { suppressReason: 'flagOff' satisfies AutoRememberMeSuppressReason })
          return
        }
        const result = await dispatch(newPersistentSession())
        if (!newPersistentSession.fulfilled.match(result)) {
          return
        }
        if (result.payload.minted) {
          markRememberMeMinted()
          return
        }
        if (result.payload.withheld) {
          logEvent(TrackingEvent.AUTO_REMEMBER_ME_SUPPRESSED, {
            suppressReason: 'serverWithheld' satisfies AutoRememberMeSuppressReason,
            withheldReason: result.payload.withheldReason,
          })
        }
      } catch {
        // Minting is best-effort: never block the post-login flow on remember-me.
      }
    })()
  }, [persistent, isTrustedCheckout, cohort, isIframe, uuid, hasFeatureFlagsReady, ldClient, dispatch, logEvent])
}
